import { useEffect, useRef } from 'react';

import { compareShadow, isShadowing } from './compareShadow';
import { logShadowMismatch } from './logShadowMismatch';

import type { PromotionContextTreatment } from './compareShadow';
import type { LogFn } from './logShadowMismatch';

interface UseShadowComparisonArgs<T> {
  label: string;
  isReady: boolean;
  treatment: PromotionContextTreatment;
  isLoading: boolean;
  primary: T;
  shadow: T;
  keys: ReadonlyArray<keyof T>;
  log: LogFn;
}

export function useShadowComparison<T>({
  label,
  isReady,
  treatment,
  isLoading,
  primary,
  shadow,
  keys,
  log,
}: UseShadowComparisonArgs<T>): void {
  const primaryValues = JSON.stringify(keys.map(key => primary[key]));
  const shadowValues = JSON.stringify(keys.map(key => shadow[key]));
  const keysKey = JSON.stringify(keys);

  const logRef = useRef(log);
  useEffect(() => {
    logRef.current = log;
  });

  useEffect(() => {
    if (!isReady) {
      return;
    }
    if (!isShadowing(treatment)) {
      return;
    }
    if (isLoading) {
      return;
    }
    const result = compareShadow({ label, primary, shadow, keys });
    logShadowMismatch(result, logRef.current);
    // primary, shadow, keys, and log are intentionally omitted — primary/shadow/keys
    // are tracked via JSON.stringify'd dep strings, and log is captured in a ref
    // updated in a sibling effect so the latest log is used without re-running
    // this effect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isReady,
    treatment,
    isLoading,
    primaryValues,
    shadowValues,
    keysKey,
    label,
  ]);
}
